import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import { Avatar, Checkbox, Tab, Tabs } from "@mui/material";
import ContentPage from "components/contentPage/ContentPage";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import { BranchFavouriteContext } from "contexts/BranchFavouriteContext";
import {
  BranchSelector,
  IBranchSelectorProps,
} from "components/branchSelector/BranchSelector";
import { useLocalBranch } from "hooks/LocalStorage";
import { useQueryCateringEvents, invalidateCateringEventsCache } from "hooks/CateringEvents";
import {
  CateringEventStatus,
  CateringEventStatusList,
  ICateringEventView,
} from "types/CateringEvent";
import { IBranch } from "types/Branch";
import { MealType } from "types/MealType";
import { format } from "date-fns";
import AddIcon from "assets/AddIcon";
import Toast, { IToastBasicProps } from "components/Toast/Toast";
import CateringPOPreview from "./CateringPOPreview";
import styles from "./CateringEvents.module.css";

const CateringEvents: React.FC = React.memo(() => {
  const { branches } = useContext(BranchFavouriteContext);
  const [selectedBranch, setSelectedBranch] = useLocalBranch(
    branches.length > 0 ? branches[0] : undefined
  );

  const today = new Date();
  const defaultStart = new Date(today);
  defaultStart.setDate(today.getDate() - 7);
  const defaultEnd = new Date(today);
  defaultEnd.setDate(today.getDate() + 30);

  const [startDate] = useState(defaultStart);
  const [endDate] = useState(defaultEnd);
  const [statusFilter, setStatusFilter] = useState<CateringEventStatus | "">("");

  if (!selectedBranch) {
    return <></>;
  }

  return (
    <ContentPage
      header={
        <Header
          branches={branches}
          selectedBranch={selectedBranch}
          setSelectedBranch={setSelectedBranch}
        />
      }
      mainContent={
        <MainSection
          startDate={startDate}
          endDate={endDate}
          branchId={selectedBranch.id}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />
      }
      rightSection={<RightSection />}
    />
  );
});

const Header: React.FC<IBranchSelectorProps> = (props) => {
  return (
    <>
      <div className={styles.pageName}>Catering</div>
      <BranchSelector
        branches={props.branches}
        selectedBranch={props.selectedBranch}
        setSelectedBranch={props.setSelectedBranch}
      />
    </>
  );
};

interface MainSectionProps {
  startDate: Date;
  endDate: Date;
  branchId: string;
  statusFilter: CateringEventStatus | "";
  onStatusFilterChange: (v: CateringEventStatus | "") => void;
}

const MainSection: React.FC<MainSectionProps> = (props) => {
  const history = useHistory();
  const { branches } = useContext(BranchFavouriteContext);
  const branchMap = new Map(branches.map((b) => [b.id, b]));

  const { status, eventsData, isFetching } = useQueryCateringEvents(
    props.startDate,
    props.endDate,
    props.branchId,
    (props.statusFilter as CateringEventStatus) || undefined
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPOPreview, setShowPOPreview] = useState(false);
  const [showToast, setShowToast] = useState<IToastBasicProps>({
    open: false,
    message: "",
    type: "success",
  });

  const events = eventsData?.cateringEvents ?? [];
  const createdEvents = events.filter((e) => e.status === CateringEventStatus.CREATED);
  const createdIds = new Set(createdEvents.map((e) => e.id));

  // Only keep selections that are still CREATED
  const activeSelectedIds = new Set([...selectedIds].filter((id) => createdIds.has(id)));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (activeSelectedIds.size === createdEvents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(createdEvents.map((e) => e.id)));
    }
  };

  if (status === "loading" || isFetching) {
    return <div className={styles.emptyState}>Loading...</div>;
  }

  return (
    <>
      <Tabs
        value={props.statusFilter}
        onChange={(_, v) => props.onStatusFilterChange(v)}
        className={styles.statusTabs}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="All" value="" />
        {CateringEventStatusList.map((s) => (
          <Tab key={s} label={s.replace(/_/g, " ")} value={s} />
        ))}
      </Tabs>
      <Toast
        open={showToast.open}
        message={showToast.message}
        type={showToast.type}
        onClose={() => setShowToast({ ...showToast, open: false })}
      />
      {activeSelectedIds.size > 0 && (
        <div style={{ padding: "8px 0", fontSize: 13, color: "#1565c0" }}>
          {activeSelectedIds.size} event(s) selected &mdash;{" "}
          <button
            onClick={() => setShowPOPreview(true)}
            style={{
              background: "none",
              border: "none",
              color: "#1565c0",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
              textDecoration: "underline",
            }}
          >
            Raise PO
          </button>
        </div>
      )}
      {events.length === 0 ? (
        <div className={styles.emptyState}>No catering events found for this date range.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCell}>
                <Checkbox
                  checked={createdEvents.length > 0 && activeSelectedIds.size === createdEvents.length}
                  indeterminate={activeSelectedIds.size > 0 && activeSelectedIds.size < createdEvents.length}
                  onChange={toggleSelectAll}
                  size="small"
                />
              </th>
              <th>Date</th>
              <th>Meal</th>
              <th>Name</th>
              <th>Customer</th>
              <th>Pax</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <EventRow
                key={e.id}
                event={e}
                branchName={branchMap.get(e.branchId)?.name ?? e.branchId}
                isSelected={activeSelectedIds.has(e.id)}
                onToggleSelect={toggleSelect}
                onClick={() => history.push(`/catering/events/${e.id}`)}
              />
            ))}
          </tbody>
        </table>
      )}
      {showPOPreview && (
        <CateringPOPreview
          eventIds={Array.from(activeSelectedIds)}
          onClose={() => setShowPOPreview(false)}
          onSuccess={(msg) => {
            setShowPOPreview(false);
            setSelectedIds(new Set());
            invalidateCateringEventsCache();
            setShowToast({ open: true, message: msg, type: "success" });
          }}
        />
      )}
    </>
  );
};

const statusStyleMap: Record<CateringEventStatus, string> = {
  [CateringEventStatus.DRAFT]: styles.statusDraft,
  [CateringEventStatus.CREATED]: styles.statusConfirmed,
  [CateringEventStatus.PO_CONSOLIDATED]: styles.statusPoRaised,
  [CateringEventStatus.DONE]: styles.statusCompleted,
};

const EventRow: React.FC<{
  event: ICateringEventView;
  branchName: string;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onClick: () => void;
}> = ({ event, branchName: _branchName, isSelected, onToggleSelect, onClick }) => {
  const isCreated = event.status === CateringEventStatus.CREATED;

  return (
    <tr className={styles.tableRow} onClick={onClick}>
      <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
        {isCreated && (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(event.id)}
            size="small"
          />
        )}
      </td>
      <td>
        <div>{format(new Date(event.eventDate), "dd MMM yyyy")}</div>
        {event.eventTimePerMeal?.[event.mealType] && (
          <div className={styles.dateSubtitle}>
            {formatTime12h(event.eventTimePerMeal[event.mealType]!)}
          </div>
        )}
      </td>
      <td>{formatMealType(event.mealType)}</td>
      <td>{event.name}</td>
      <td>{event.customerName}</td>
      <td>{event.numberOfPeople}</td>
      <td>
        <span className={`${styles.statusChip} ${statusStyleMap[event.status]}`}>
          {event.status.replace(/_/g, " ")}
        </span>
      </td>
    </tr>
  );
};

function formatMealType(mt: MealType): string {
  return mt.charAt(0) + mt.slice(1).toLowerCase();
}

function formatTime12h(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? "AM" : "PM";
  return `${h12}:${mStr} ${ampm}`;
}

const RightSection: React.FC = () => {
  const { primaryColor } = useContext(FMSThemeContext);
  const history = useHistory();

  return (
    <div>
      <button
        type="button"
        className={styles.button}
        onClick={() => history.push("/catering/events/new")}
      >
        <Avatar className={styles.icon} style={{ backgroundColor: primaryColor }}>
          <AddIcon />
        </Avatar>
        <div className={styles.buttonTitle}>Create Event</div>
      </button>
    </div>
  );
};

export default CateringEvents;
